import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Hammer } from 'lucide-react';
import { api } from '../lib/api.ts';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select, Textarea } from '../components/ui/Input.tsx';
import { Badge, TemplateStatusBadge, ChannelBadge } from '../components/ui/Badge.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Template {
  id: string;
  name: string;
  channel: 'email' | 'sms' | 'push' | 'webhook';
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  status: 'draft' | 'active' | 'archived';
  variables?: string[] | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
        {icon && <span className="text-zinc-400">{icon}</span>}
        <h2 className="text-sm font-semibold text-zinc-700">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-50 last:border-0">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-800 font-medium text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [showEdit, setShowEdit] = useState(false);
  const [showHtml, setShowHtml] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    channel: 'email' as Template['channel'],
    subject: '',
    bodyText: '',
    bodyHtml: '',
  });

  const { data: tmpl, isLoading } = useQuery<Template | null>({
    queryKey: ['template', id],
    queryFn: () =>
      api.get(`/api/v1/templates/${id}`).then((r) => {
        const d = r.data;
        if (!d || typeof d !== 'object' || !d.id) return null;
        return d as Template;
      }),
    enabled: !!id,
  });

  const activateMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/templates/${id}/activate`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['template', id] });
      qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => api.delete(`/api/v1/templates/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      navigate('/templates');
    },
  });

  const editMutation = useMutation({
    mutationFn: (data: typeof editForm) => api.patch(`/api/v1/templates/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['template', id] });
      qc.invalidateQueries({ queryKey: ['templates'] });
      setShowEdit(false);
    },
  });

  const openEdit = () => {
    if (!tmpl) return;
    setEditForm({
      name: tmpl.name,
      channel: tmpl.channel,
      subject: tmpl.subject ?? '',
      bodyText: tmpl.bodyText ?? '',
      bodyHtml: tmpl.bodyHtml ?? '',
    });
    setShowEdit(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!tmpl) {
    return (
      <div className="text-center py-16 text-zinc-400">
        <FileText size={32} className="mx-auto mb-3 opacity-30" />
        <p className="text-sm">Template not found</p>
        <button onClick={() => navigate('/templates')} className="mt-4 text-xs text-zinc-500 underline">
          Back to Templates
        </button>
      </div>
    );
  }

  const variables: string[] = Array.isArray(tmpl.variables) ? tmpl.variables : [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/templates')}
          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-zinc-900 truncate">{tmpl.name}</h1>
        </div>
        <TemplateStatusBadge status={tmpl.status} />
        {tmpl.channel === 'email' && (
          <Button
            size="sm"
            onClick={() => navigate(`/templates/${tmpl.id}/edit`)}
          >
            <Hammer size={13} />
            Open Builder
          </Button>
        )}
        {tmpl.status === 'draft' && (
          <Button size="sm" variant="secondary" loading={activateMutation.isPending} onClick={() => activateMutation.mutate()}>
            Activate
          </Button>
        )}
        {tmpl.status !== 'archived' && (
          <Button
            size="sm"
            variant="ghost"
            loading={archiveMutation.isPending}
            onClick={() => { if (confirm(`Archive "${tmpl.name}"?`)) archiveMutation.mutate(); }}
          >
            Archive
          </Button>
        )}
        <Button size="sm" onClick={openEdit}>Edit</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column */}
        <div className="lg:col-span-1 space-y-5">
          <Section title="Details" icon={<FileText size={14} />}>
            <div className="space-y-0">
              <KV label="ID" value={<code className="font-mono text-[10px]">{tmpl.id}</code>} />
              <KV label="Channel" value={<ChannelBadge channel={tmpl.channel} />} />
              <KV label="Status" value={<TemplateStatusBadge status={tmpl.status} />} />
              <KV label="Created" value={fmt(tmpl.createdAt)} />
              <KV label="Updated" value={fmt(tmpl.updatedAt)} />
            </div>
          </Section>

          {variables.length > 0 && (
            <Section title="Variables">
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <Badge key={v} variant="outline">
                    <code className="font-mono text-[10px]">{`{{${v}}}`}</code>
                  </Badge>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-5">
          {tmpl.channel === 'email' && tmpl.subject && (
            <Section title="Subject">
              <p className="text-sm text-zinc-800 font-medium">{tmpl.subject}</p>
            </Section>
          )}

          {tmpl.bodyText && (
            <Section title="Body (plain text)">
              <pre className="text-xs font-mono text-zinc-700 whitespace-pre-wrap break-words leading-relaxed max-h-64 overflow-y-auto">
                {tmpl.bodyText}
              </pre>
            </Section>
          )}

          {tmpl.channel === 'email' && tmpl.bodyHtml && (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-3.5 border-b border-zinc-100 text-left"
                onClick={() => setShowHtml((v) => !v)}
              >
                <h2 className="text-sm font-semibold text-zinc-700">Body (HTML)</h2>
                <span className="text-xs text-zinc-400">{showHtml ? 'collapse' : 'expand'}</span>
              </button>
              {showHtml && (
                <div className="p-5">
                  <pre className="text-xs font-mono text-zinc-700 whitespace-pre-wrap break-words leading-relaxed max-h-96 overflow-y-auto">
                    {tmpl.bodyHtml}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title="Edit Template" size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Select
              label="Channel"
              value={editForm.channel}
              onChange={(e) => setEditForm((f) => ({ ...f, channel: e.target.value as Template['channel'] }))}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="push">Push</option>
              <option value="webhook">Webhook</option>
            </Select>
          </div>
          {editForm.channel === 'email' && (
            <Input
              label="Subject"
              value={editForm.subject}
              onChange={(e) => setEditForm((f) => ({ ...f, subject: e.target.value }))}
            />
          )}
          <Textarea
            label={editForm.channel === 'email' ? 'Body (plain text)' : 'Message body'}
            rows={5}
            value={editForm.bodyText}
            onChange={(e) => setEditForm((f) => ({ ...f, bodyText: e.target.value }))}
          />
          {editForm.channel === 'email' && (
            <Textarea
              label="Body (HTML) — optional"
              rows={5}
              value={editForm.bodyHtml}
              onChange={(e) => setEditForm((f) => ({ ...f, bodyHtml: e.target.value }))}
            />
          )}
          {editMutation.isError && (
            <p className="text-xs text-red-500">
              {(editMutation.error as { message?: string })?.message ?? 'Error saving template'}
            </p>
          )}
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button
              size="sm"
              loading={editMutation.isPending}
              disabled={!editForm.name}
              onClick={() => editMutation.mutate(editForm)}
            >
              Save changes
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
}
